import { Check, ChevronLeft, ChevronRight, List, Sun, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { MadeIt } from '@/components/recipes/MadeIt'
import { OriginalPhoto } from '@/components/OriginalPhoto'
import { cookPages, stepTo } from '@/lib/cook'
import { photoUrl } from '@/lib/photos'
import { cardPhotos, type Recipe } from '@/lib/recipes'
import { cn } from '@/lib/utils'

const round =
  'grid size-11 shrink-0 place-items-center rounded-full outline-none transition-colors hover:bg-viewer-foreground/12 focus-visible:ring-[3px] focus-visible:ring-ring/60'

// Modern Cook mode: the recipe one page at a time in a dark room, big enough to read from across the counter. First
// what to get out, then each step as it was written, then "That's everything". Swipe, the arrow keys or the buttons
// move between pages, and the bars along the top jump to any of them. The card is a tap away in the corner for when
// the typed words aren't clear. Ticks are just for this sitting and aren't saved.
export function CookSteps({ recipe, awake }: { recipe: Recipe; awake: boolean }) {
  const pages = cookPages(recipe)
  const [at, setAt] = useState(0)
  const [back, setBack] = useState(false)
  const [ticked, setTicked] = useState<number[]>([])
  const [listing, setListing] = useState(false)
  const card = cardPhotos(recipe)[0]
  const page = pages[at]
  const go = (by: number) => {
    setBack(by < 0)
    setAt((now) => stepTo(now, by, pages.length))
  }
  const jump = (to: number) => {
    setBack(to < at)
    setAt(to)
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.target instanceof HTMLInputElement) return
      if (e.key === 'ArrowRight' || e.key === ' ') go(1)
      else if (e.key === 'ArrowLeft') go(-1)
      else return
      e.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  // A sideways swipe turns the page; an up-and-down one is left to scrolling a long step.
  const start = useRef<{ x: number; y: number } | null>(null)
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse') start.current = { x: e.clientX, y: e.clientY }
  }
  const onPointerUp = (e: React.PointerEvent) => {
    const from = start.current
    start.current = null
    if (!from) return
    const dx = e.clientX - from.x
    if (Math.abs(dx) > 56 && Math.abs(dx) > Math.abs(e.clientY - from.y) * 1.4) go(dx < 0 ? 1 : -1)
  }

  const stepCount = pages.filter((x) => x.kind === 'step').length
  return (
    <div
      data-testid="cook-steps"
      className="fixed inset-0 z-50 flex flex-col bg-viewer text-viewer-foreground"
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
    >
      <header className="flex items-center gap-2 px-3 pt-[calc(var(--safe-top)+0.75rem)] pb-3 sm:px-5">
        <Link to={`/recipe/${recipe.id}`} aria-label="Stop cooking" className={round}>
          <X aria-hidden />
        </Link>
        <div className="min-w-0 flex-1 leading-tight">
          <h1 className="truncate font-semibold">{recipe.title}</h1>
          {awake && (
            <p className="flex items-center gap-1.5 text-sm text-viewer-muted">
              <Sun className="size-3.5" aria-hidden /> The screen stays on
            </p>
          )}
        </div>
        {card && (
          <OriginalPhoto
            src={photoUrl(card.id, 'original')}
            fallback={photoUrl(card.ref, 'display')}
            alt={`The original card for ${recipe.title}`}
          >
            <button
              type="button"
              aria-label="Show the card"
              className="h-12 w-16 shrink-0 overflow-hidden rounded-lg ring-1 ring-viewer-foreground/20 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60"
            >
              <img src={photoUrl(card.ref, 'thumb')} alt="" className="size-full object-cover" />
            </button>
          </OriginalPhoto>
        )}
      </header>

      {pages.length > 1 && !listing && (
        <nav aria-label="Pages" className="flex gap-1 px-5">
          {pages.map((p, i) => (
            <button
              key={i}
              type="button"
              onClick={() => jump(i)}
              aria-label={p.kind === 'step' ? `Step ${p.n}` : p.kind === 'ingredients' ? 'Ingredients' : 'The end'}
              aria-current={i === at ? 'step' : undefined}
              className="group flex h-6 flex-1 items-center outline-none"
            >
              <span
                className={cn(
                  'h-1 w-full rounded-full transition-colors duration-300 group-focus-visible:ring-2 group-focus-visible:ring-ring/60',
                  i <= at ? 'bg-viewer-foreground' : 'bg-viewer-foreground/20',
                )}
              />
            </button>
          ))}
        </nav>
      )}

      {/* Clipped sideways: each page slides in from the side, which would otherwise flash a scrollbar under it. Long
          words wrap, so nothing is lost to the clip. */}
      <main className="relative min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain wrap-anywhere">
        {listing ? (
          <WholeRecipe recipe={recipe} ticked={ticked} onTick={setTicked} />
        ) : !page ? (
          <p className="mx-auto max-w-2xl px-6 pt-10 text-2xl leading-snug">
            This recipe is kept as a photo of the card. Show the card to cook from it.
          </p>
        ) : (
          <div
            key={at}
            aria-live="polite"
            className={cn(
              'mx-auto flex max-w-2xl flex-col gap-6 px-6 pt-8 pb-10 motion-safe:animate-in motion-safe:duration-300 motion-safe:fade-in-0',
              back ? 'motion-safe:slide-in-from-left-6' : 'motion-safe:slide-in-from-right-6',
            )}
          >
            {page.kind === 'ingredients' && (
              <>
                <p className="eyebrow text-viewer-muted">Get these out</p>
                <Ticks lines={page.lines} ticked={ticked} onTick={setTicked} large />
              </>
            )}
            {page.kind === 'step' && (
              <>
                <p className="eyebrow text-viewer-muted">
                  Step {page.n} of {stepCount}
                </p>
                <p className="text-[clamp(1.75rem,6.5vw,2.75rem)] leading-[1.22] font-medium tracking-tight text-pretty">
                  {page.text}
                </p>
              </>
            )}
            {page.kind === 'done' && (
              <>
                <p className="text-[clamp(1.75rem,6.5vw,2.75rem)] leading-[1.22] font-medium tracking-tight">
                  That’s everything.
                </p>
                <p className="text-lg text-viewer-muted">
                  Add a photo of yours and it joins the recipe under Made by others.
                </p>
                <div className="flex flex-wrap gap-3">
                  <MadeIt recipeId={recipe.id} className="h-14 bg-viewer-foreground px-7 text-base text-viewer">
                    Add a photo of yours
                  </MadeIt>
                  <Link
                    to={`/recipe/${recipe.id}`}
                    className="inline-flex h-14 items-center rounded-full bg-viewer-foreground/10 px-7 text-base font-medium outline-none hover:bg-viewer-foreground/16 focus-visible:ring-[3px] focus-visible:ring-ring/60"
                  >
                    Done
                  </Link>
                </div>
              </>
            )}
          </div>
        )}
      </main>

      {pages.length > 0 && (
        <footer className="grid grid-cols-[auto_1fr_1.6fr] gap-2.5 px-4 pt-2 pb-[calc(var(--safe-bottom)+0.75rem)]">
          <button
            type="button"
            aria-label="Previous"
            disabled={listing || at === 0}
            onClick={() => go(-1)}
            className="grid h-14 w-14 place-items-center rounded-full bg-viewer-foreground/10 outline-none hover:bg-viewer-foreground/16 focus-visible:ring-[3px] focus-visible:ring-ring/60 disabled:opacity-35"
          >
            <ChevronLeft aria-hidden />
          </button>
          <button
            type="button"
            aria-pressed={listing}
            onClick={() => setListing((l) => !l)}
            className="inline-flex h-14 items-center justify-center gap-2 rounded-full bg-viewer-foreground/10 text-base font-medium outline-none hover:bg-viewer-foreground/16 focus-visible:ring-[3px] focus-visible:ring-ring/60"
          >
            <List className="size-5" aria-hidden /> {listing ? 'One at a time' : 'Whole recipe'}
          </button>
          <button
            type="button"
            disabled={listing || at === pages.length - 1}
            onClick={() => go(1)}
            className="inline-flex h-14 items-center justify-center gap-2 rounded-full bg-viewer-foreground text-base font-semibold text-viewer outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60 disabled:opacity-35"
          >
            {page?.kind === 'ingredients' ? 'Start' : 'Next'} <ChevronRight className="size-5" aria-hidden />
          </button>
        </footer>
      )}
    </div>
  )
}

function Ticks({
  lines,
  ticked,
  onTick,
  large,
}: {
  lines: string[]
  ticked: number[]
  onTick: (fn: (t: number[]) => number[]) => void
  large?: boolean
}) {
  return (
    <ul className="flex flex-col">
      {lines.map((line, i) => {
        const on = ticked.includes(i)
        return (
          <li key={i}>
            <label
              className={cn(
                'flex min-h-12 cursor-pointer items-start gap-4 rounded-2xl px-2 py-2.5 has-focus-visible:ring-[3px] has-focus-visible:ring-ring/60',
                large && 'text-xl',
              )}
            >
              <span className="relative mt-0.5 grid size-7 shrink-0 place-items-center">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => onTick((t) => (t.includes(i) ? t.filter((x) => x !== i) : [...t, i]))}
                  className="peer size-7 cursor-pointer appearance-none rounded-lg border-2 border-viewer-foreground/35 outline-none checked:border-viewer-foreground checked:bg-viewer-foreground"
                />
                <Check
                  aria-hidden
                  strokeWidth={3}
                  className="pointer-events-none absolute size-4 text-viewer opacity-0 peer-checked:opacity-100"
                />
              </span>
              <span className={cn('transition-colors', on && 'text-viewer-muted line-through')}>{line}</span>
            </label>
          </li>
        )
      })}
    </ul>
  )
}

// Everything on one page, for a glance ahead: the same ticks, and the steps numbered.
function WholeRecipe({
  recipe,
  ticked,
  onTick,
}: {
  recipe: Recipe
  ticked: number[]
  onTick: (fn: (t: number[]) => number[]) => void
}) {
  const pages = cookPages(recipe)
  const ingredients = pages.find((p) => p.kind === 'ingredients')
  const steps = pages.flatMap((p) => (p.kind === 'step' ? [p] : []))
  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-8 px-6 pt-6 pb-10 text-lg">
      {ingredients?.kind === 'ingredients' && (
        <section className="flex flex-col gap-2">
          <h2 className="eyebrow text-viewer-muted">Ingredients</h2>
          <Ticks lines={ingredients.lines} ticked={ticked} onTick={onTick} />
        </section>
      )}
      {steps.length > 0 && (
        <section className="flex flex-col gap-3">
          <h2 className="eyebrow text-viewer-muted">Steps</h2>
          <ol className="flex flex-col gap-4">
            {steps.map((s) => (
              <li key={s.n} className="flex gap-4">
                <span aria-hidden className="w-6 shrink-0 font-semibold text-viewer-muted">
                  {s.n}
                </span>
                <span>{s.text}</span>
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  )
}
